// components/glossary-tooltip.tsx
"use client";

import { motion } from "framer-motion";
import { ReactNode } from "react";

interface BrandTakeProps {
  children: ReactNode;
}

export function BrandTake({ children }: BrandTakeProps) {
  return (
    <motion.div 
      className="my-12 p-8 bg-bg-dark rounded-xl border-l-4 border-teal"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-2xl font-display font-bold text-white mb-4">
        Fluxos AI 'Howthef' Take
      </h2>
      <div className="text-gray-300 prose prose-invert max-w-none">
        {children}
      </div>
    </motion.div>
  );
}

interface GlossaryTermProps {
  term: string;
  definition: string;
}

export function GlossaryTerm({ term, definition }: GlossaryTermProps) {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-display font-bold text-teal mb-2">{term}</h3>
      <p className="text-gray-300">{definition}</p>
    </div>
  );
}

interface GlossaryTermsProps {
  terms: Array<{
    term: string;
    definition: string;
  }>;
}

export function GlossaryTerms({ terms }: GlossaryTermsProps) {
  return (
    <div className="">
      <h2 className="text-2xl font-display font-bold text-white mb-2">
        Key Terms Explained
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {terms.map((item, index) => (
          <motion.div
            key={index}
            className="bg-bg-dark p-6 rounded-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <GlossaryTerm term={item.term} definition={item.definition} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

interface GlossaryTooltipProps {
  term: string;
  definition: string;
  children: ReactNode;
}

export function GlossaryTooltip({ term, definition, children }: GlossaryTooltipProps) {
  return (
    <div className="group relative inline-block cursor-help">
      <span className="border-b border-dotted border-teal">{children}</span>
      <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-4 bg-black border border-medium-gray rounded-lg shadow-lg z-50">
        <div className="flex flex-col">
          <span className="font-bold text-teal">{term}</span>
          <span className="text-sm text-gray-300 mt-1">{definition}</span>
        </div>
        <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-black border-r border-b border-medium-gray" />
      </div>
    </div>
  );
}